/**
 * Record Exchange - inbound request inbox API (Phase 3).
 */
import apiClient from "@/lib/api-client";

export type RecordExchangeFlow = "disclosure" | "receipt";
export type RecordExchangeStatus =
  | "pending"
  | "approved"
  | "sending"
  | "matching"
  | "querying"
  | "received"
  | "review_ready"
  | "importing"
  | "imported"
  | "failed"
  | "denied"
  | "fulfilled"
  | "cancelled";

export type RecordExchangePurpose =
  | "referral"
  | "transition"
  | "patient_request"
  | "legal"
  | "other";

export interface RecordExchangeRequest {
  id: string;
  organization_id: string;
  flow: RecordExchangeFlow;
  operation: "disclosure_push" | "receipt_import" | "data_request";
  status: RecordExchangeStatus;
  patient_id: string;
  patient_name?: string;
  patient_mrn?: string;
  partner_id?: string | null;
  partner_name?: string | null;
  requestor_name: string;
  requestor_org?: string | null;
  requestor_email?: string | null;
  purpose: string;
  format: string;
  selected_resource_types?: string[];
  package_manifest?: FhirResourceManifest[];
  package_sha256?: string | null;
  remote_patient_id?: string | null;
  query_started_at?: string | null;
  received_at?: string | null;
  import_completed_at?: string | null;
  source?: string;
  external_reference?: string | null;
  notes?: string | null;
  has_inbound_payload: boolean;
  reviewed_by_user_id?: string | null;
  reviewed_at?: string | null;
  denial_reason?: string | null;
  fulfilled_by_user_id?: string | null;
  fulfilled_at?: string | null;
  fulfillment_detail?: string | null;
  delivery_attempts?: RecordExchangeDeliveryAttempt[];
  query_attempts?: RecordExchangeQueryAttempt[];
  import_attempts?: RecordExchangeImportAttempt[];
  created_by_user_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface FhirResourceManifest {
  resource_type: string;
  count: number;
}

export type RecordExchangeDeliveryAttemptStatus = "succeeded" | "failed";

export interface RecordExchangeDeliveryAttempt {
  id: string;
  request_id: string;
  attempt_number: number;
  partner_id: string;
  transport: string;
  status: RecordExchangeDeliveryAttemptStatus;
  package_sha256: string;
  http_status?: number | null;
  error_code?: string | null;
  error_message?: string | null;
  started_at: string;
  completed_at: string;
  created_at: string;
}

export interface RecordExchangeQueryAttempt {
  id: string;
  request_id: string;
  attempt_number: number;
  partner_id: string;
  status: string;
  response_sha256?: string | null;
  resource_count: number;
  http_status?: number | null;
  safe_error_code?: string | null;
  started_at: string;
  completed_at: string;
  created_at: string;
}

export interface RecordExchangeImportAttempt {
  id: string;
  request_id: string;
  attempt_number: number;
  status: string;
  selected_count: number;
  imported_count: number;
  skipped_count: number;
  failed_count: number;
  safe_error_code?: string | null;
  started_at: string;
  completed_at: string;
  created_at: string;
}

export interface CreateDataRequestInput {
  patient_id: string;
  partner_id: string;
  purpose: string;
  notes?: string;
  selected_resource_types?: string[];
}

export interface ConfirmPatientMatchInput {
  remote_patient_id: string;
  identifier_system?: string;
  identifier_value?: string;
  match_method: string;
  confidence: number;
}

export interface PatientMatchCandidate {
  remote_patient_id: string;
  identifiers?: { system: string; value: string }[];
  display: string;
  birth_date?: string;
  gender?: string;
  confidence: number;
}

export type FHIRCandidateClassification =
  | "new"
  | "duplicate"
  | "changed"
  | "unsupported"
  | "conflict";

export interface FHIRImportCandidate {
  id: string;
  resource_type: string;
  remote_resource_id: string;
  remote_version_id?: string | null;
  resource_sha256: string;
  classification: FHIRCandidateClassification;
  decision?: string | null;
  preview?: Record<string, unknown>;
  safe_error_code?: string | null;
}

export interface FHIRStagedResult {
  request_id: string;
  status: string;
  payload_sha256: string;
  manifest: FhirResourceManifest[];
  resource_count: number;
  expires_at: string;
  candidates: FHIRImportCandidate[];
}

export interface ExecuteQueryResponse {
  request_id: string;
  status: string;
  match_required: boolean;
  matches?: PatientMatchCandidate[];
  staged?: FHIRStagedResult;
}

export interface ImportFHIRResponse {
  request_id: string;
  status: string;
  attempt?: RecordExchangeImportAttempt;
}

export interface RecordExchangeListResponse {
  requests: RecordExchangeRequest[];
  page: number;
  per_page: number;
  total_count: number;
}

export interface RecordExchangeStats {
  pending: number;
  approved: number;
  failed: number;
  total: number;
}

export interface CreateRecordExchangeInput {
  flow: RecordExchangeFlow;
  patient_id: string;
  partner_id?: string;
  requestor_name: string;
  requestor_org?: string;
  requestor_email?: string;
  purpose: string;
  format?: string;
  notes?: string;
  inbound_ccda_xml?: string;
  selected_resource_types?: string[];
}

export interface FulfillRecordExchangeResponse {
  status: RecordExchangeStatus;
  fulfillment_detail: string;
  attempt?: RecordExchangeDeliveryAttempt;
}

export const FLOW_LABELS: Record<RecordExchangeFlow, string> = {
  disclosure: "Disclosure - send records out",
  receipt: "Receipt - import records in",
};

export const STATUS_META: Record<
  RecordExchangeStatus,
  { label: string; classes: string }
> = {
  pending: {
    label: "Pending review",
    classes: "bg-amber-500/10 text-amber-700 border-amber-400/30",
  },
  approved: {
    label: "Approved",
    classes: "bg-sky-500/10 text-sky-700 border-sky-400/30",
  },
  sending: {
    label: "Sending",
    classes: "bg-indigo-500/10 text-indigo-700 border-indigo-400/30",
  },
  matching: {
    label: "Confirm patient match",
    classes: "bg-amber-500/10 text-amber-700 border-amber-400/30",
  },
  querying: {
    label: "Querying partner",
    classes: "bg-indigo-500/10 text-indigo-700 border-indigo-400/30",
  },
  received: {
    label: "Records received",
    classes: "bg-sky-500/10 text-sky-700 border-sky-400/30",
  },
  review_ready: {
    label: "Ready for review",
    classes: "bg-violet-500/10 text-violet-700 border-violet-400/30",
  },
  importing: {
    label: "Importing",
    classes: "bg-indigo-500/10 text-indigo-700 border-indigo-400/30",
  },
  imported: {
    label: "Imported",
    classes: "bg-emerald-500/10 text-emerald-700 border-emerald-400/30",
  },
  failed: {
    label: "Delivery failed",
    classes: "bg-rose-500/10 text-rose-700 border-rose-400/30",
  },
  denied: {
    label: "Denied",
    classes: "bg-rose-500/10 text-rose-700 border-rose-400/30",
  },
  fulfilled: {
    label: "Fulfilled",
    classes: "bg-emerald-500/10 text-emerald-700 border-emerald-400/30",
  },
  cancelled: {
    label: "Cancelled",
    classes: "bg-muted text-muted-foreground border-border",
  },
};

export const PURPOSE_OPTIONS = [
  { id: "referral", label: "Referral" },
  { id: "transition", label: "Transition of Care" },
  { id: "patient_request", label: "Patient Request" },
  { id: "legal", label: "Legal / Compliance" },
  { id: "other", label: "Other" },
] as const;

export async function listRecordExchangeRequests(params?: {
  page?: number;
  per_page?: number;
  status?: RecordExchangeStatus;
  flow?: RecordExchangeFlow;
  patient_id?: string;
  search?: string;
}): Promise<RecordExchangeListResponse> {
  const res = await apiClient.get<{ data: RecordExchangeListResponse }>(
    "/record-exchange/requests",
    { params },
  );
  return res.data.data;
}

export async function getRecordExchangeStats(): Promise<RecordExchangeStats> {
  const res = await apiClient.get<{ data: RecordExchangeStats }>(
    "/record-exchange/requests/stats",
  );
  return res.data.data;
}

export async function getRecordExchangeRequest(
  id: string,
): Promise<RecordExchangeRequest> {
  const res = await apiClient.get<{ data: RecordExchangeRequest }>(
    `/record-exchange/requests/${id}`,
  );
  return res.data.data;
}

export async function createRecordExchangeRequest(
  input: CreateRecordExchangeInput,
): Promise<RecordExchangeRequest> {
  const res = await apiClient.post<{ data: RecordExchangeRequest }>(
    "/record-exchange/requests",
    input,
    { headers: { "Idempotency-Key": crypto.randomUUID() } },
  );
  return res.data.data;
}

export async function createDataRequest(
  input: CreateDataRequestInput,
): Promise<RecordExchangeRequest> {
  const res = await apiClient.post<{ data: RecordExchangeRequest }>(
    "/record-exchange/data-requests",
    input,
    { headers: { "Idempotency-Key": crypto.randomUUID() } },
  );
  return res.data.data;
}

export async function confirmDataRequestMatch(
  id: string,
  input: ConfirmPatientMatchInput,
): Promise<RecordExchangeRequest> {
  const res = await apiClient.post<{ data: RecordExchangeRequest }>(
    `/record-exchange/requests/${id}/confirm-match`,
    input,
  );
  return res.data.data;
}

export async function executeDataRequestQuery(id: string): Promise<ExecuteQueryResponse> {
  const res = await apiClient.post<{ data: ExecuteQueryResponse }>(
    `/record-exchange/requests/${id}/execute-query`,
  );
  return res.data.data;
}

export async function retryDataRequestQuery(id: string): Promise<ExecuteQueryResponse> {
  const res = await apiClient.post<{ data: ExecuteQueryResponse }>(
    `/record-exchange/requests/${id}/retry-query`,
  );
  return res.data.data;
}

export async function getStagedDataRequest(id: string): Promise<FHIRStagedResult> {
  const res = await apiClient.get<{ data: FHIRStagedResult }>(
    `/record-exchange/requests/${id}/staged`,
  );
  return res.data.data;
}

export async function importDataRequestCandidates(
  id: string,
  importCandidateIds: string[],
): Promise<ImportFHIRResponse> {
  const res = await apiClient.post<{ data: ImportFHIRResponse }>(
    `/record-exchange/requests/${id}/import`,
    { import_candidate_ids: importCandidateIds },
  );
  return res.data.data;
}

export async function approveRecordExchangeRequest(
  id: string,
): Promise<RecordExchangeRequest> {
  const res = await apiClient.post<{ data: RecordExchangeRequest }>(
    `/record-exchange/requests/${id}/approve`,
  );
  return res.data.data;
}

export async function denyRecordExchangeRequest(
  id: string,
  reason: string,
): Promise<RecordExchangeRequest> {
  const res = await apiClient.post<{ data: RecordExchangeRequest }>(
    `/record-exchange/requests/${id}/deny`,
    { reason },
  );
  return res.data.data;
}

export async function fulfillRecordExchangeRequest(
  id: string,
): Promise<FulfillRecordExchangeResponse> {
  const res = await apiClient.post<{ data: FulfillRecordExchangeResponse }>(
    `/record-exchange/requests/${id}/fulfill`,
  );
  return res.data.data;
}

export async function retryRecordExchangeRequest(
  id: string,
): Promise<FulfillRecordExchangeResponse> {
  const res = await apiClient.post<{ data: FulfillRecordExchangeResponse }>(
    `/record-exchange/requests/${id}/retry`,
  );
  return res.data.data;
}

export async function cancelRecordExchangeRequest(
  id: string,
): Promise<RecordExchangeRequest> {
  const res = await apiClient.post<{ data: RecordExchangeRequest }>(
    `/record-exchange/requests/${id}/cancel`,
  );
  return res.data.data;
}

export type InternalExchangeDirection = "sent" | "received";
export interface InternalExchangeReceipt {
  correlation_id: string;
  status: string;
  final: boolean;
  code: string;
  retryable: boolean;
  occurred_at: string;
}
export interface InternalExchangeRequest {
  exchange_id: string;
  connection_id: string;
  counterparty_organization_id: string;
  direction: InternalExchangeDirection;
  status: string;
  version: number;
  purpose: string;
  resource_types: string[];
  sensitivities?: string[];
  receipt?: InternalExchangeReceipt;
  created_at: string;
  updated_at: string;
}
export interface InternalExchangeList {
  requests: InternalExchangeRequest[];
  page: number;
  per_page: number;
  total_count: number;
}

const internalPath = "/record-exchange/internal";

export async function createInternalExchangeRequest(input: {
  holder_organization_id: string;
  connection_id: string;
  patient_id: string;
  patient_identifier_system: string;
  patient_identifier: string;
  purpose: string;
  resource_types: string[];
  sensitivities?: string[];
}) {
  const response = await apiClient.post<{ data: InternalExchangeRequest }>(
    `${internalPath}/requests`,
    input,
    { headers: { "Idempotency-Key": crypto.randomUUID() } },
  );
  return response.data.data;
}

export async function listInternalExchangeRequests(
  direction: InternalExchangeDirection,
  status?: string,
) {
  const response = await apiClient.get<{ data: InternalExchangeList }>(
    `${internalPath}/${direction}`,
    { params: { status, per_page: 100 } },
  );
  return response.data.data;
}

export async function getInternalExchangeRequest(exchangeId: string) {
  const response = await apiClient.get<{ data: InternalExchangeRequest }>(
    `${internalPath}/requests/${exchangeId}`,
  );
  return response.data.data;
}

export async function issueInternalMatchProof(exchangeId: string, patientId: string) {
  const response = await apiClient.post<{
    data: { exchange_id: string; proof: string; expires_at: string };
  }>(`${internalPath}/requests/${exchangeId}/match-proof`, { patient_id: patientId });
  return response.data.data;
}

export async function confirmInternalPatientMatch(
  exchangeId: string,
  patientId: string,
  proof: string,
  version: number,
) {
  const response = await apiClient.post<{ data: InternalExchangeRequest }>(
    `${internalPath}/requests/${exchangeId}/confirm-match`,
    { patient_id: patientId, proof, version },
  );
  return response.data.data;
}

export async function attestInternalExchange(
  exchangeId: string,
  version: number,
  legalBasis: string,
  consentEvidence: Record<string, unknown>,
) {
  const response = await apiClient.post<{ data: InternalExchangeRequest }>(
    `${internalPath}/requests/${exchangeId}/attest`,
    { version, legal_basis: legalBasis, consent_evidence: consentEvidence },
  );
  return response.data.data;
}

async function internalAction(
  exchangeId: string,
  action: "approve" | "fulfill" | "retry" | "cancel",
  version: number,
) {
  const response = await apiClient.post<{ data: InternalExchangeRequest }>(
    `${internalPath}/requests/${exchangeId}/${action}`,
    { version },
  );
  return response.data.data;
}

export const approveInternalExchange = (id: string, version: number) =>
  internalAction(id, "approve", version);
export const fulfillInternalExchange = (id: string, version: number) =>
  internalAction(id, "fulfill", version);
export const retryInternalExchange = (id: string, version: number) =>
  internalAction(id, "retry", version);
export const cancelInternalExchange = (id: string, version: number) =>
  internalAction(id, "cancel", version);

export async function denyInternalExchange(id: string, version: number, code: string) {
  const response = await apiClient.post<{ data: InternalExchangeRequest }>(
    `${internalPath}/requests/${id}/deny`,
    { version, code },
  );
  return response.data.data;
}
